(() => {
  const money = (cents, format) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'pt-PT', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    }).format(cents / 100);
  };

  const parseJson = (root, selector, fallback = []) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || JSON.stringify(fallback));
    } catch (error) {
      console.error('Personalization JSON parse failed', error);
      return fallback;
    }
  };

  const countLetters = (value) => {
    try {
      return Array.from((value || '').normalize('NFC')).filter((char) => /\p{L}/u.test(char)).length;
    } catch (_) {
      return ((value || '').match(/[A-Za-zÀ-ÖØ-öø-ÿ]/g) || []).length;
    }
  };

  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const form = document.getElementById(root.dataset.formId);
    if (!form) return;

    const nameInput = root.querySelector('.artes-personalization__field--name input[type="text"]');
    const letterCountEl = root.querySelector('[data-letter-count]');
    const letterPriceEl = root.querySelector('[data-letter-price]');
    const totalEl = root.querySelector('[data-personalization-total]');
    const errorEl = root.querySelector('[data-personalization-error]');
    const variantInput = form.querySelector('.product-variant-id');
    const variants = parseJson(root, '[data-personalization-variants]');
    let chargeVariants = parseJson(root, '[data-personalization-charge-variants]');
    const chargeProductUrl = root.dataset.chargeProductUrl || '';
    const namePricingEnabled = root.dataset.namePricingEnabled !== 'false';
    const includedLetters = Math.max(0, Number(root.dataset.includedLetters || 3));
    const maxLetters = Math.max(includedLetters, Number(root.dataset.maxLetters || 26));

    // Product-setting Liquid can be stale in an unpublished theme immediately after
    // a merchant adds a new variant. Refresh the technical product from Shopify
    // before checkout so newly-created charging variants are always recognised.
    const refreshChargeVariants = async () => {
      if (!chargeProductUrl) return chargeVariants;
      try {
        const separator = chargeProductUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${chargeProductUrl}${separator}_=${Date.now()}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) return chargeVariants;
        const productData = await response.json();
        if (Array.isArray(productData?.variants)) chargeVariants = productData.variants;
      } catch (error) {
        console.warn('Could not refresh personalization charge variants', error);
      }
      return chargeVariants;
    };
    const letterPrices = parseJson(root, '[data-personalization-letter-prices]', {});
    const cartUrl = root.dataset.cartUrl || '/cart';
    const moneyFormat = root.dataset.moneyFormat || '{{amount_with_comma_separator}} €';

    const getBasePrice = () => {
      const currentId = Number(variantInput?.value || root.dataset.baseVariantId);
      const variant = variants.find((item) => Number(item.id) === currentId);
      return Number(variant?.price || root.dataset.basePrice || 0);
    };

    const findChargeVariant = (price) => {
      const target = Math.round(Number(price || 0));
      if (target <= 0) return null;

      return chargeVariants.find((variant) => {
        const raw = Number(variant?.price);
        if (!Number.isFinite(raw)) return false;

        // Shopify product JSON exposes variant.price in cents (for example 800 = €8.00).
        // A few integrations can expose decimal euros instead, so accept that format only
        // when the numeric value is below €1 in cents-space. Do NOT infer a price from
        // variant titles/options: labels such as "Letra Bubble 8 cm adicional" contain
        // numbers that are measurements, not prices, and previously caused €8 varnish
        // to be matched to the €1.50 letter helper.
        if (Math.round(raw) === target) return true;
        if (raw > 0 && raw < 100 && Math.round(raw * 100) === target) return true;
        return false;
      }) || null;
    };

    const getLetterSelection = () => {
      if (!namePricingEnabled) return { count: 0, extraCount: 0, unitPrice: 0, price: 0, chargeVariant: null };
      const count = countLetters(nameInput?.value || '');
      const extraCount = Math.max(0, count - includedLetters);
      const unitPrice = Number(letterPrices.unit || 0);
      const price = extraCount * unitPrice;
      return { count, extraCount, unitPrice, price, chargeVariant: findChargeVariant(unitPrice) };
    };

    const getSelectedAddons = () => {
      const addons = [];
      const letters = getLetterSelection();
      if (letters.count > includedLetters) {
        addons.push({
          kind: 'letters',
          price: letters.price,
          unitPrice: letters.unitPrice,
          extraCount: letters.extraCount,
          label: `${letters.extraCount} ${letters.extraCount === 1 ? 'letra adicional' : 'letras adicionais'}`,
          letterCount: letters.count,
          chargeVariant: letters.chargeVariant,
        });
      }
      root.querySelectorAll('[data-extra-checkbox]:checked').forEach((checkbox) => {
        const price = Number(checkbox.dataset.price || 0);
        addons.push({
          kind: 'extra',
          slot: checkbox.dataset.extraSlot,
          price,
          label: checkbox.dataset.label || checkbox.closest('label')?.innerText.trim() || 'Extra',
          chargeVariant: findChargeVariant(price),
        });
      });
      return addons;
    };

    const updateLetterSummary = () => {
      const letters = getLetterSelection();
      if (letterCountEl) letterCountEl.textContent = `${letters.count} ${letters.count === 1 ? 'letra' : 'letras'}`;
      if (letterPriceEl) letterPriceEl.textContent = letters.price > 0 ? `+${money(letters.price, moneyFormat)}` : money(0, moneyFormat);
    };

    const updateTotal = () => {
      updateLetterSummary();
      const addonsTotal = getSelectedAddons().reduce((sum, addon) => sum + addon.price, 0);
      const total = getBasePrice() + addonsTotal;
      if (totalEl) totalEl.textContent = money(total, moneyFormat);

      const priceRoot = document.getElementById(`price-${root.dataset.sectionId}`);
      if (priceRoot) {
        priceRoot.querySelectorAll('.price-item--regular:not(.variant-item__old-price)').forEach((el) => { el.textContent = money(total, moneyFormat); });
        priceRoot.querySelectorAll('.price-item--sale').forEach((el) => { el.textContent = money(total, moneyFormat); });
        const currentVariant = variants.find((item) => Number(item.id) === Number(variantInput?.value || root.dataset.baseVariantId));
        const compare = Number(currentVariant?.compare_at_price || 0);
        if (compare > 0) {
          priceRoot.querySelectorAll('.price-item--regular.variant-item__old-price').forEach((el) => { el.textContent = money(compare + addonsTotal, moneyFormat); });
          priceRoot.querySelectorAll('.price__sale .price-item--regular').forEach((el) => { el.textContent = money(compare + addonsTotal, moneyFormat); });
        }
      }
    };

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    };
    const clearError = () => {
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
      if (nameInput) nameInput.removeAttribute('aria-invalid');
    };

    const validateName = () => {
      if (!namePricingEnabled) return true;
      const rawName = (nameInput?.value || '').trim();
      const letterCount = countLetters(rawName);
      if (!rawName || letterCount === 0) {
        if (nameInput) {
          nameInput.setAttribute('aria-invalid', 'true');
          nameInput.setCustomValidity('Por favor, escreva o nome para personalizar.');
          nameInput.reportValidity();
          nameInput.focus();
        }
        showError('Por favor, escreva o nome para personalizar.');
        return false;
      }
      if (letterCount > maxLetters) {
        if (nameInput) {
          nameInput.setAttribute('aria-invalid', 'true');
          nameInput.setCustomValidity(`O nome pode ter no máximo ${maxLetters} letras.`);
          nameInput.reportValidity();
          nameInput.focus();
        }
        showError(`O nome pode ter no máximo ${maxLetters} letras. Espaços e sinais não contam.`);
        return false;
      }
      if (nameInput) nameInput.setCustomValidity('');
      return true;
    };

    const validateCharges = (addons) => {
      for (const addon of addons) {
        if (addon.kind === 'letters' && addon.letterCount > includedLetters && addon.unitPrice <= 0) {
          return `Configuração incompleta: não existe um preço definido para ${addon.letterCount} letras.`;
        }
        if ((addon.kind === 'letters' ? addon.unitPrice : addon.price) > 0 && !addon.chargeVariant) {
          return `Configuração incompleta: não existe uma variante de cobrança com o preço ${money(addon.kind === 'letters' ? addon.unitPrice : addon.price, moneyFormat)}. Contacte a loja antes de concluir a compra.`;
        }
      }
      return '';
    };

    nameInput?.addEventListener('input', () => {
      if (nameInput) nameInput.setCustomValidity('');
      clearError();
      updateTotal();
    });
    root.querySelectorAll('[data-extra-checkbox]').forEach((checkbox) => checkbox.addEventListener('change', () => { clearError(); updateTotal(); }));

    let lastVariant = variantInput?.value;
    const variantWatcher = window.setInterval(() => {
      if (!document.documentElement.contains(root)) { window.clearInterval(variantWatcher); return; }
      const currentVariant = variantInput?.value;
      if (currentVariant && currentVariant !== lastVariant) { lastVariant = currentVariant; updateTotal(); }
    }, 250);
    updateTotal();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      // Dawn has its own submit listener on this same form. Without this, it can
      // still add only the base product after our validation blocks.
      event.stopImmediatePropagation();
      clearError();

      if (!validateName()) return;
      if (!form.reportValidity()) return;

      await refreshChargeVariants();
      const addons = getSelectedAddons();
      const chargeError = validateCharges(addons);
      if (chargeError) { showError(chargeError); return; }

      const submitButton = form.querySelector('[type="submit"]');
      submitButton?.setAttribute('aria-busy', 'true');
      submitButton?.classList.add('loading');

      try {
        const formData = new FormData(form);
        const personalizationGroup = `artes-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const mainVariantId = Number(formData.get('id'));
        if (!mainVariantId) throw new Error('Não foi possível identificar o produto.');
        const quantityInput = form.querySelector('[name="quantity"]');
        const quantity = Math.max(1, Number(quantityInput?.value || 1));
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && value !== '') {
            const propertyName = key.slice(11, -1);
            properties[propertyName] = value;
          }
        }

        const letters = getLetterSelection();
        const items = [{
          id: mainVariantId,
          quantity,
          properties: {
            ...properties,
            ...(namePricingEnabled ? {
              'Número de letras': `${letters.count} ${letters.count === 1 ? 'letra' : 'letras'}`,
              ...(letters.extraCount > 0 ? { 'Letras adicionais': `${letters.extraCount} × ${money(letters.unitPrice, moneyFormat)} = ${money(letters.price, moneyFormat)}` } : {}),
            } : {}),
            _artes_personalization_group: personalizationGroup,
            ...Object.fromEntries(addons.filter((addon) => addon.kind === 'extra').map((addon) => [`Extra ${addon.slot}`, `${addon.label} (+${money(addon.price, moneyFormat)})`])),
          },
        }];

        addons.forEach((addon) => {
          if (addon.price <= 0 || !addon.chargeVariant?.id) return;
          const isLetters = addon.kind === 'letters';
          const visibleLabel = isLetters
            ? `${addon.extraCount} ${addon.extraCount === 1 ? 'letra adicional' : 'letras adicionais'} × ${money(addon.unitPrice, moneyFormat)} = ${money(addon.price, moneyFormat)}`
            : `${addon.label} (+${money(addon.price, moneyFormat)})`;
          const multiplier = isLetters ? addon.extraCount : 1;
          items.push({
            id: Number(addon.chargeVariant.id),
            quantity: quantity * multiplier,
            properties: {
              _artes_personalization_label: visibleLabel,
              _artes_personalization_addon: 'true',
              _artes_personalization_parent: String(mainVariantId),
              _artes_personalization_group: personalizationGroup,
              _artes_personalization_multiplier: String(multiplier),
            },
          });
        });

        // Store the expected helper multipliers on the parent line so the cart
        // checkout guard can independently verify the real Shopify cart before
        // allowing checkout. Values are per one parent item; cart quantities are
        // multiplied during validation.
        const expectedHelpers = {};
        items.slice(1).forEach((item) => {
          const variantId = String(Number(item.id));
          const multiplier = Math.max(1, Number(item.properties?._artes_personalization_multiplier || 1));
          expectedHelpers[variantId] = (expectedHelpers[variantId] || 0) + multiplier;
        });
        items[0].properties._artes_personalization_guard_version = '1';
        items[0].properties._artes_personalization_expected_helpers = JSON.stringify(expectedHelpers);

        const rootPath = window.Shopify?.routes?.root || '/';
        const addItems = async (itemsToAdd) => {
          const response = await fetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: itemsToAdd }),
          });
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.description || 'Não foi possível adicionar o produto ao carrinho.');
          }
          return response.json().catch(() => ({}));
        };

        const fetchCart = async () => {
          const response = await fetch(`${rootPath}cart.js?_=${Date.now()}`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          if (!response.ok) throw new Error('Não foi possível confirmar o carrinho.');
          return response.json();
        };

        const groupItems = (cart) => (cart?.items || []).filter(
          (item) => item.properties?._artes_personalization_group === personalizationGroup
        );

        const expectedAddonQuantities = new Map();
        items.slice(1).forEach((item) => {
          const variantId = Number(item.id);
          expectedAddonQuantities.set(
            variantId,
            (expectedAddonQuantities.get(variantId) || 0) + Number(item.quantity || 0)
          );
        });

        const getMissingAddonItems = (cart) => {
          const grouped = groupItems(cart);
          const actual = new Map();
          grouped
            .filter((item) => item.properties?._artes_personalization_addon === 'true')
            .forEach((item) => {
              const variantId = Number(item.variant_id || item.id);
              actual.set(variantId, (actual.get(variantId) || 0) + Number(item.quantity || 0));
            });

          const missing = [];
          expectedAddonQuantities.forEach((expectedQty, variantId) => {
            const missingQty = expectedQty - (actual.get(variantId) || 0);
            if (missingQty <= 0) return;
            const source = items.slice(1).find((item) => Number(item.id) === variantId);
            if (source) missing.push({ ...source, quantity: missingQty });
          });
          return missing;
        };

        const groupIsComplete = (cart) => {
          const grouped = groupItems(cart);
          const mainQuantity = grouped
            .filter((item) => item.properties?._artes_personalization_addon !== 'true' && Number(item.variant_id || item.id) === mainVariantId)
            .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
          if (mainQuantity < quantity) return false;
          return getMissingAddonItems(cart).length === 0;
        };

        const rollbackGroup = async () => {
          try {
            const cart = await fetchCart();
            const updates = {};
            groupItems(cart).forEach((item) => { updates[item.key] = 0; });
            if (Object.keys(updates).length) {
              await fetch(`${rootPath}cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ updates }),
              });
            }
          } catch (rollbackError) {
            console.error('Could not roll back incomplete personalization group', rollbackError);
          }
        };

        // Add the complete group first. Then verify the actual Shopify cart rather
        // than trusting only the /cart/add.js success response. This prevents a
        // personalised product from ever reaching checkout without its charges.
        await addItems(items);
        let cart = await fetchCart();

        // If Shopify did not persist one or more helper lines from the batch, retry
        // only the missing helper quantities once, preserving this group's ID.
        let missingAddons = getMissingAddonItems(cart);
        if (missingAddons.length) {
          await addItems(missingAddons);
          cart = await fetchCart();
          missingAddons = getMissingAddonItems(cart);
        }

        if (!groupIsComplete(cart) || missingAddons.length) {
          await rollbackGroup();
          throw new Error('Não foi possível aplicar todos os valores da personalização. O produto não foi adicionado. Tente novamente ou contacte a loja.');
        }

        window.location.assign(cartUrl);
      } catch (error) {
        console.error(error);
        showError(error.message || 'Ocorreu um erro. Tente novamente.');
        submitButton?.removeAttribute('aria-busy');
        submitButton?.classList.remove('loading');
      }
    }, true);
  };

  const boot = () => document.querySelectorAll('[data-personalization]').forEach(init);
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
  if (document.readyState !== 'loading') boot();
})();
